import cn from 'clsx'
import Image from 'next/legacy/image'
import { FC } from 'react'
import { GiftBoxIcon, GiftExchangeIcon, GiftReceiptIcon } from './gift-icons'

const features = [
  { Icon: GiftBoxIcon, label: 'Luxe gift box/wrapping with card included' },
  { Icon: GiftReceiptIcon, label: 'Gift receipt provided' },
  { Icon: GiftExchangeIcon, label: 'Exchange available in-store or online' },
]

interface Props {
  /**
   * When the desktop grid is toggled to 6 columns the tile is too narrow for
   * the feature list, so on desktop it uses a condensed layout: the feature
   * icons are hidden and the "add gift wrapping" line moves to the bottom.
   * The mobile layout is unaffected.
   */
  sixColumnDesktop?: boolean
}

/**
 * Gifting promo tile shown inside the PLP product grid.
 * - Mobile: spans the full grid width, shorter aspect ratio (336/420).
 * - Desktop (4-col): single product cell (aspect 304/524). Full layout at xl+;
 *   between lg and xl it uses the condensed layout (no feature icons) because a
 *   single cell is too narrow there.
 * - Desktop (6-col): condensed cell (aspect 192/384) at all lg+ widths — no
 *   feature icons, the gift-wrapping line moves to the bottom.
 * Full-bleed background image with white copy; height is driven by the
 * intrinsic aspect ratio + `h-full` so it matches its grid row.
 */
const GiftingCard: FC<Props> = ({ sixColumnDesktop = false }) => {
  // The condensed layout (no feature icons; gift-wrapping line at the bottom)
  // is used at lg+ for the 6-col grid, and only between lg and xl for the
  // 4-col grid — a single 4-col cell is too narrow for the feature list there.
  const featuresClass = sixColumnDesktop ? 'lg:hidden' : 'lg:hidden xl:flex'
  const topPriceClass = sixColumnDesktop ? 'lg:hidden' : 'lg:hidden xl:block'
  const bottomPriceClass = sixColumnDesktop
    ? 'hidden lg:block'
    : 'hidden lg:block xl:hidden'

  return (
    <div
      className={cn(
        'relative flex h-full w-full flex-col justify-between overflow-hidden aspect-[336/420] text-white',
        sixColumnDesktop ? 'lg:aspect-[192/384]' : 'lg:aspect-[304/524]'
      )}
    >
      <Image
        src="/gifting_image.jpg"
        alt=""
        layout="fill"
        objectFit="cover"
        sizes="(min-width: 1024px) 304px, 100vw"
        unoptimized
      />
      <div className="absolute inset-0 bg-black/[0.15]" />

      <div
        className={cn(
          'relative z-10 flex flex-col gap-2 px-[14px] pt-8 text-center',
          sixColumnDesktop ? 'lg:pt-6' : 'lg:pt-10'
        )}
      >
        <p className="text-24 leading-8 font-bold lg:text-20 lg:leading-7 xl:text-24 xl:leading-8">
          Gifting made easy
        </p>
        <p className={cn('text-16 leading-6', topPriceClass)}>
          A special moment or just because. Add the finishing touch.
        </p>
        {/* Hidden in the condensed layout — moves to the bottom instead. */}
        <p className={cn('text-13 leading-5', topPriceClass)}>
          Add gift wrapping in cart for $6.95
        </p>
      </div>

      {/* Feature list — hidden in the condensed layout. */}
      <ul
        className={cn(
          'relative z-10 flex w-full flex-col gap-2 px-[14px] pb-8 text-13 leading-5 font-bold lg:pb-10',
          featuresClass
        )}
      >
        {features.map(({ Icon, label }) => (
          <li key={label} className="flex items-center gap-2">
            <Icon className="h-5 w-5 shrink-0" />
            <span>{label}</span>
          </li>
        ))}
      </ul>

      {/* Condensed layout: gift-wrapping line at the bottom, centred. */}
      <p
        className={cn(
          'relative z-10 px-[14px] pb-10 text-center text-13 leading-5',
          bottomPriceClass
        )}
      >
        Add gift wrapping in cart for $6.95
      </p>
    </div>
  )
}

export default GiftingCard
